import React, { useMemo } from 'react';
import { isBefore } from "date-fns";
import { cloneDeep } from "lodash";
import useTranslations from "../../hooks/useTranslations";
import useViewer from "../../hooks/useViewer";
import ReportPlate from "./ReportPlate";
import { ReviewFullFragment } from '../../graphql/generated/definitions';

type UserFollowUpReportsProps = {
  review: ReviewFullFragment;
};

const UserFollowUpReports: React.FC<UserFollowUpReportsProps> = ({ review }) => {
  const t = useTranslations();

  const viewer = useViewer();

  const filledReports = useMemo(() => {
    if (viewer.id === review.collaborator.id) {
      return (review.reports)
    }
    return (
      (review.reports || []).filter(({ filledAt }) => filledAt)
    );
  }, [viewer, review]);

  const orderedReports = useMemo(() => {
    return cloneDeep(filledReports).sort((lhs, rhs) =>
      isBefore(new Date(lhs.createdAt), new Date(rhs.createdAt)) ? 1 : -1
    );
  }, [filledReports]);

  const hasSignatures = useMemo(() => (
    !!orderedReports.find(({ docusignEnvelopeStatus }) => docusignEnvelopeStatus)
  ), [orderedReports]);

  return (
    <div className="flex flex-col">
      {orderedReports.length === 0 ? (
        <div className="flex justify-center text-sm text-light-900">
          {t("reviews.noReports")}
        </div>
      ) : (
        <div className="-my-2 overflow-x-auto">
          <div className="inline-block min-w-full py-2 align-middle">
            <div className="overflow-hidden border-b border-gray-200 shadow sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                    >
                      {t("reviews.reportsSection.name")}
                    </th>
                    {
                      hasSignatures && (
                        <th
                          scope="col"
                          className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                        >
                          {t("reviews.reportsSection.signature")}
                        </th>
                      )
                    }
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                    >
                      {t("reviews.reportsSection.status")}
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orderedReports.map((report) => (
                    <ReportPlate
                      report={report}
                      user={review.collaborator}
                      review={review}
                      key={report.id}
                      showSignature={hasSignatures}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserFollowUpReports;
